import { supabase } from "@/integrations/supabase/client";
import { analyticsAllowed } from "@/lib/consent";

type UserType = "customer" | "admin" | "anonymous";

interface TrackParams {
  event_type: string;
  module?: string;
  entity_type?: string;
  entity_id?: string;
  city?: string;
  metadata?: Record<string, any>;
}

// Privacy-safe metadata: drop anything that looks sensitive.
const SAFE_KEYS_BLOCKLIST = /password|token|secret|message|note|body|cover_letter|resume|email|phone|address|name(?!_)/i;
function safeMeta(meta?: Record<string, any>): Record<string, any> {
  if (!meta) return {};
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(meta)) {
    if (SAFE_KEYS_BLOCKLIST.test(k)) continue;
    if (typeof v === "string" && v.length > 200) {
      out[k] = v.slice(0, 200);
    } else {
      out[k] = v;
    }
  }
  return out;
}

export async function trackEvent(params: TrackParams, userTypeHint?: UserType): Promise<void> {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const user = auth.user ?? null;
    let userType: UserType = userTypeHint ?? "anonymous";
    if (user && !userTypeHint) {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .limit(1);
      userType = roles && roles.length > 0 ? "admin" : "customer";
    }

    await supabase.from("portal_activity_events").insert({
      user_id: user?.id ?? null,
      user_email: user?.email ?? null,
      user_type: userType,
      event_type: params.event_type,
      module: params.module ?? null,
      entity_type: params.entity_type ?? null,
      entity_id: params.entity_id ?? null,
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
      city: params.city ?? null,
      metadata: safeMeta(params.metadata),
    } as any);
  } catch {
    // Never break the UI for analytics
  }
}

// ---------- Public site helpers ----------

const PUBLIC_PREFIX_BLOCKLIST = ["/cws-admin", "/dashboard", "/onboarding", "/auth", "/login", "/signup", "/reset-password"];

function isPublicPath(path: string): boolean {
  return !PUBLIC_PREFIX_BLOCKLIST.some((p) => path === p || path.startsWith(p + "/"));
}

function deviceCategory(): "mobile" | "tablet" | "desktop" {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function referrerDomain(): string | null {
  try {
    if (typeof document === "undefined" || !document.referrer) return null;
    const u = new URL(document.referrer);
    if (u.hostname === window.location.hostname) return null;
    return u.hostname;
  } catch {
    return null;
  }
}

function utmParams(): Record<string, string> {
  try {
    const sp = new URLSearchParams(window.location.search);
    const out: Record<string, string> = {};
    ["utm_source", "utm_medium", "utm_campaign"].forEach((k) => {
      const v = sp.get(k);
      if (v) out[k] = v.slice(0, 60);
    });
    return out;
  } catch {
    return {};
  }
}

const seenPaths = new Set<string>();

export async function trackPublicPageView(path: string, opts?: { city?: string; route_name?: string }) {
  try {
    if (typeof window === "undefined") return;
    if (!isPublicPath(path)) return;
    if (!analyticsAllowed()) return;
    const key = path + window.location.search;
    if (seenPaths.has(key)) return;
    seenPaths.add(key);

    await trackEvent({
      event_type: "public_page_viewed",
      module: "public_site",
      entity_type: "page",
      city: opts?.city,
      metadata: {
        page_title: typeof document !== "undefined" ? document.title?.slice(0, 120) : null,
        route_name: opts?.route_name,
        referrer_domain: referrerDomain(),
        device: deviceCategory(),
        ...utmParams(),
      },
    }, "anonymous");
  } catch {}
}

export async function trackPublicCta(label: string, opts?: { target_path?: string; city?: string; service_type?: string }) {
  try {
    if (!analyticsAllowed()) return;
    await trackEvent({
      event_type: "public_cta_clicked",
      module: "public_site",
      entity_type: "cta",
      city: opts?.city,
      metadata: {
        cta_label: label.slice(0, 80),
        source_page: typeof window !== "undefined" ? window.location.pathname : null,
        target_path: opts?.target_path,
        service_type: opts?.service_type,
        device: deviceCategory(),
      },
    }, "anonymous");
  } catch {}
}

export async function trackPublicForm(
  stage: "started" | "submitted",
  formType: string,
  opts?: { city?: string; customer_type?: string }
) {
  try {
    await trackEvent({
      event_type: stage === "started" ? "public_form_started" : "public_form_submitted",
      module: "public_site",
      entity_type: "form",
      city: opts?.city,
      metadata: {
        form_type: formType,
        source_page: typeof window !== "undefined" ? window.location.pathname : null,
        customer_type: opts?.customer_type,
        device: deviceCategory(),
      },
    }, "anonymous");
  } catch {}
}
